import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Plus, Pencil, Trash2, LogOut, Save, X, Loader2, ArrowLeft } from "lucide-react";
import { BLOG_CATEGORIES } from "../data/mock";
import {
  adminLogin,
  setToken,
  getToken,
  clearToken,
  fetchPosts,
  createPost,
  updatePost,
  deletePost,
} from "../lib/api";
import Seo from "../components/Seo";
import ImageUpload from "../components/ImageUpload";
import RichEditor from "../components/RichEditor";
import ReferencesAdmin from "../components/admin/ReferencesAdmin";
import InboxAdmin from "../components/admin/InboxAdmin";
import { imgUrl, fetchContactStats } from "../lib/api";

const emptyForm = {
  id: null,
  slug: "",
  category: "strategy",
  image: "",
  author: "AdalCrea",
  read_time: 5,
  date: "",
  title: { tr: "", en: "" },
  excerpt: { tr: "", en: "" },
  content: { tr: "", en: "" },
};

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
    .replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const Admin = () => {
  const [authed, setAuthed] = useState(!!getToken());
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null); // null = list view
  const [tab, setTab] = useState("blog");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({ new: 0, total: 0 });

  const loadStats = () => fetchContactStats().then(setStats).catch(() => {});

  const load = () => {
    setLoading(true);
    fetchPosts()
      .then(setPosts)
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (authed) {
      load();
      loadStats();
    }
  }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const token = await adminLogin(password);
      setToken(token);
      setAuthed(true);
    } catch (err) {
      setLoginError("Hatalı şifre / Invalid password");
    }
  };

  const handleLogout = () => {
    clearToken();
    setAuthed(false);
    setPassword("");
  };

  const startCreate = () => {
    setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) });
    setError("");
  };

  const startEdit = (p) => {
    setForm({
      id: p.id,
      slug: p.slug,
      category: p.category,
      image: p.image,
      author: p.author,
      read_time: p.read_time,
      date: p.date,
      title: { ...p.title },
      excerpt: { ...p.excerpt },
      content: { ...p.content },
    });
    setError("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.content.tr || !form.content.en) {
      setError("İçerik (TR ve EN) zorunludur / Content is required");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      slug: form.slug || slugify(form.title.tr || form.title.en),
      category: form.category,
      image: form.image,
      author: form.author || "AdalCrea",
      read_time: Number(form.read_time) || 5,
      date: form.date || new Date().toISOString().slice(0, 10),
      title: form.title,
      excerpt: form.excerpt,
      content: form.content,
    };
    try {
      if (form.id) {
        await updatePost(form.id, payload);
      } else {
        await createPost(payload);
      }
      setForm(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Kayıt başarısız / Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`"${p.title.tr}" silinsin mi? / Delete this post?`)) return;
    try {
      await deletePost(p.id);
      load();
    } catch (err) {
      alert("Silme başarısız / Delete failed");
    }
  };

  const set = (path, val) => {
    setForm((f) => {
      const next = { ...f };
      if (path.includes(".")) {
        const [k, sub] = path.split(".");
        next[k] = { ...next[k], [sub]: val };
      } else {
        next[path] = val;
      }
      return next;
    });
  };

  const inputCls =
    "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-navy-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-colors";
  const labelCls = "block text-sm font-medium text-navy-900 mb-1.5";

  // ---------- Login ----------
  if (!authed) {
    return (
      <>
        <Seo title="Yönetim Paneli" description="AdalCrea admin" path="/admin" />
        <div className="min-h-screen bg-navy-950 flex items-center justify-center px-5 grid-pattern">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center">
                <Lock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h1 className="font-display font-bold text-xl text-navy-900">Yönetim Paneli</h1>
                <p className="text-sm text-slate-500">Blog içerik yönetimi</p>
              </div>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className={labelCls}>Şifre / Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  autoFocus
                />
              </div>
              {loginError && <p className="text-sm text-red-500">{loginError}</p>}
              <button
                type="submit"
                className="w-full bg-amber-500 hover:bg-amber-400 text-navy-950 font-semibold py-3 rounded-lg transition-colors"
              >
                Giriş Yap
              </button>
            </form>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-600">
              <ArrowLeft className="w-4 h-4" /> Siteye dön
            </Link>
          </div>
        </div>
      </>
    );
  }

  // ---------- Editor ----------
  if (form) {
    return (
      <div className="min-h-screen bg-slate-50 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-5">
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-display font-bold text-2xl text-navy-900">
              {form.id ? "Yazıyı Düzenle" : "Yeni Yazı"}
            </h1>
            <button onClick={() => setForm(null)} className="text-slate-500 hover:text-navy-900 flex items-center gap-1.5">
              <X className="w-5 h-5" /> Kapat
            </button>
          </div>

          <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Başlık (TR) *</label>
                <input required value={form.title.tr} onChange={(e) => set("title.tr", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Title (EN) *</label>
                <input required value={form.title.en} onChange={(e) => set("title.en", e.target.value)} className={inputCls} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Slug (URL)</label>
                <input
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value)}
                  placeholder={slugify(form.title.tr || "otomatik")}
                  className={inputCls}
                  disabled={!!form.id}
                />
              </div>
              <div>
                <label className={labelCls}>Kategori</label>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                  {BLOG_CATEGORIES.filter((c) => c.id !== "all").map((c) => (
                    <option key={c.id} value={c.id}>{c.label.tr} / {c.label.en}</option>
                  ))}
                </select>
              </div>
            </div>

            <ImageUpload label="Kapak Görseli" value={form.image} onChange={(v) => set("image", v)} testId="blog-image-upload" />

            <div className="grid sm:grid-cols-3 gap-5">
              <div>
                <label className={labelCls}>Yazar</label>
                <input value={form.author} onChange={(e) => set("author", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Okuma (dk)</label>
                <input type="number" value={form.read_time} onChange={(e) => set("read_time", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Tarih</label>
                <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Özet (TR) *</label>
                <textarea required rows={3} value={form.excerpt.tr} onChange={(e) => set("excerpt.tr", e.target.value)} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className={labelCls}>Excerpt (EN) *</label>
                <textarea required rows={3} value={form.excerpt.en} onChange={(e) => set("excerpt.en", e.target.value)} className={`${inputCls} resize-none`} />
              </div>
            </div>

            <div className="grid gap-5">
              <div>
                <label className={labelCls}>İçerik (TR) *</label>
                <RichEditor value={form.content.tr} onChange={(v) => set("content.tr", v)} testId="editor-tr" />
              </div>
              <div>
                <label className={labelCls}>Content (EN) *</label>
                <RichEditor value={form.content.en} onChange={(v) => set("content.en", v)} testId="editor-en" />
              </div>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-70 text-navy-950 font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                Kaydet
              </button>
              <button type="button" onClick={() => setForm(null)} className="px-6 py-3 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
                İptal
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ---------- List ----------
  const tabCls = (id) =>
    `px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
      tab === id ? "bg-navy-900 text-white" : "text-slate-600 hover:bg-slate-200"
    }`;

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-5">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-bold text-2xl text-navy-900">Yönetim Paneli</h1>
            <div className="mt-3 inline-flex gap-1 rounded-xl bg-slate-100 p-1">
              <button onClick={() => setTab("blog")} className={tabCls("blog")} data-testid="admin-tab-blog">Blog ({posts.length})</button>
              <button onClick={() => setTab("refs")} className={tabCls("refs")} data-testid="admin-tab-references">Referanslar</button>
              <button onClick={() => setTab("inbox")} className={`${tabCls("inbox")} inline-flex items-center gap-2`} data-testid="admin-tab-inbox">
                Talepler
                {stats.new > 0 && <span className="rounded-full bg-amber-500 text-navy-950 text-[11px] font-bold px-1.5 py-0.5 leading-none" data-testid="inbox-new-badge">{stats.new}</span>}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {tab === "blog" && (
              <button onClick={startCreate} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-navy-950 font-semibold px-5 py-2.5 rounded-lg transition-colors" data-testid="new-post-btn">
                <Plus className="w-4 h-4" /> Yeni Yazı
              </button>
            )}
            <button onClick={handleLogout} className="inline-flex items-center gap-2 text-slate-500 hover:text-red-500 px-3 py-2.5" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Çıkış
            </button>
          </div>
        </div>

        {tab === "refs" ? (
          <ReferencesAdmin />
        ) : tab === "inbox" ? (
          <InboxAdmin onStatsChange={loadStats} />
        ) : loading ? (
          <div className="flex justify-center py-20 text-slate-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
            {posts.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4">
                <img src={imgUrl(p.image)} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-navy-900 truncate">{p.title.tr}</h3>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5">
                      {BLOG_CATEGORIES.find((c) => c.id === p.category)?.label.tr || p.category}
                    </span>
                    <span>{p.date}</span>
                    <span>/{p.slug}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => startEdit(p)} className="p-2 rounded-lg text-slate-500 hover:text-amber-600 hover:bg-amber-50" title="Düzenle">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(p)} className="p-2 rounded-lg text-slate-500 hover:text-red-500 hover:bg-red-50" title="Sil">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {posts.length === 0 && (
              <p className="p-8 text-center text-slate-400">Henüz yazı yok.</p>
            )}
          </div>
        )}

        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-600">
          <ArrowLeft className="w-4 h-4" /> Blog'a git
        </Link>
      </div>
    </div>
  );
};

export default Admin;
